import { useCallback, useEffect, useRef, useState } from 'react';
import type { MessagePage, MessageSummary, SearchRequest, SearchResultPage } from './api';

interface Snapshot extends MessagePage { key: string }

/** Mail paging keeps its visible rows while refreshing, with one request chain per folder. */
export function useMailRefreshList(
  loadPage: (offset: number) => Promise<MessagePage>, key: string, onError: (error: unknown) => void,
) {
  const [snapshot, setSnapshot] = useState<Snapshot>();
  const [busy, setBusy] = useState<{ key: string; kind: 'initial' | 'more' | 'refresh' }>();
  const [failure, setFailure] = useState<{ key: string; error: unknown }>();
  const callbacks = useRef({ loadPage, onError });
  const latest = useRef<Snapshot | undefined>(undefined);
  const generation = useRef<object>({});
  const pending = useRef<object | undefined>(undefined);
  const removed = useRef<{ key: string; ids: Set<string>; revision: number }>({ key, ids: new Set(), revision: 0 });
  useEffect(() => { callbacks.current = { loadPage, onError }; });

  useEffect(() => {
    const token = {};
    generation.current = token;
    pending.current = token;
    latest.current = undefined;
    removed.current = { key, ids: new Set(), revision: removed.current.revision + 1 };
    callbacks.current.loadPage(0).then(page => {
      if (generation.current !== token) return;
      latest.current = { ...page, key };
      setSnapshot(latest.current);
      setFailure(undefined);
    }, (error: unknown) => {
      if (generation.current === token) setFailure({ key, error });
    }).finally(() => {
      if (pending.current === token) { pending.current = undefined; setBusy(undefined); }
    });
    return () => { generation.current = {}; };
  }, [key]);

  const run = useCallback(async (kind: 'more' | 'refresh') => {
    if (pending.current) return false;
    const before = latest.current?.key === key ? latest.current : undefined;
    if (kind === 'more' && (!before || before.nextOffset === null)) return false;
    const token = {};
    const active = generation.current;
    const removalRevision = removed.current.revision;
    pending.current = token;
    setBusy({ key, kind });
    try {
      const offset = kind === 'more' ? before!.nextOffset! : 0;
      let page = await callbacks.current.loadPage(offset);
      if (generation.current !== active) return false;
      let items = kind === 'more' ? [...before!.items, ...page.items] : [...page.items];
      // Retain the loaded extent rather than collapsing to page one. A finite row target
      // and monotonically increasing offsets bound the refresh even during arrivals.
      const extent = before?.items.length ?? 0;
      let lastOffset = offset;
      let requests = 1;
      while (kind === 'refresh' && items.length < extent && page.nextOffset !== null && page.nextOffset > lastOffset && requests < Math.max(1, extent)) {
        requests += 1;
        lastOffset = page.nextOffset;
        page = await callbacks.current.loadPage(lastOffset);
        if (generation.current !== active) return false;
        items.push(...page.items);
      }
      const tombstones = removed.current.key === key ? removed.current.ids : new Set<string>();
      const removedFromResponse = new Set(items.filter(item => tombstones.has(item.id)).map(item => item.id)).size;
      if (kind === 'refresh' && removalRevision === removed.current.revision && removedFromResponse === 0) tombstones.clear();
      const seen = new Set<string>();
      items = items.filter(item => { if (seen.has(item.id) || tombstones.has(item.id)) return false; seen.add(item.id); return true; });
      latest.current = { ...page, items, key, total: Math.max(0, page.total - removedFromResponse),
        nextOffset: page.nextOffset === null ? null : Math.max(0, page.nextOffset - removedFromResponse) };
      setSnapshot(latest.current);
      setFailure(undefined);
      return true;
    } catch (error) {
      if (generation.current !== active) return false;
      if (!before) setFailure({ key, error });
      callbacks.current.onError(error);
      return false;
    } finally {
      if (pending.current === token) { pending.current = undefined; setBusy(undefined); }
    }
  }, [key]);

  const refresh = useCallback(() => run('refresh'), [run]);
  const loadMore = useCallback(() => { void run('more'); }, [run]);
  const mutate = useCallback((change: (value: Snapshot) => Snapshot) => {
    if (latest.current?.key !== key) return;
    latest.current = change(latest.current);
    setSnapshot(latest.current);
  }, [key]);
  const replace = useCallback((item: MessageSummary) => mutate(value => ({ ...value, items: value.items.map(row => row.id === item.id ? item : row) })), [mutate]);
  const remove = useCallback((id: string | number) => {
    if (removed.current.key !== key) return;
    removed.current.ids.add(String(id));
    removed.current.revision += 1;
    mutate(value => {
      const wasLoaded = value.items.some(item => item.id === id);
      return { ...value, items: value.items.filter(item => item.id !== id), total: Math.max(0, value.total - Number(wasLoaded)),
        nextOffset: value.nextOffset === null ? null : Math.max(0, value.nextOffset - Number(wasLoaded)) };
    });
  }, [key, mutate]);
  const value = snapshot?.key === key ? snapshot : undefined;
  const activity = busy?.key === key ? busy.kind : undefined;
  const loading = !value && failure?.key !== key;
  return {
    items: value?.items ?? [], total: value?.total ?? 0,
    loading,
    loadingMore: activity === 'more', refreshing: activity === 'refresh', busy: activity !== undefined || loading,
    error: failure?.key === key ? failure.error : undefined,
    hasMore: value?.nextOffset != null, loadMore, refresh, reload: refresh, replace, remove,
  };
}

interface SearchSnapshot { key: string; page?: SearchResultPage; failed: boolean }

/** One cursor chain per search identity; refresh retains results until the new chain settles. */
export function useMailSearchRefresh(
  request: SearchRequest | undefined, load: (request: SearchRequest) => Promise<SearchResultPage>, onError: (error: unknown) => void,
) {
  const key = JSON.stringify(request ?? null);
  const callbacks = useRef({ request, load, onError });
  const latest = useRef<SearchSnapshot | undefined>(undefined);
  const generation = useRef<object>({});
  const pending = useRef<object | undefined>(undefined);
  const [snapshot, setSnapshot] = useState<SearchSnapshot>();
  const [activity, setActivity] = useState<{ key: string; kind: 'refresh' | 'more' }>();
  const removed = useRef<{ key: string; ids: Set<string>; revision: number }>({ key, ids: new Set(), revision: 0 });
  useEffect(() => { callbacks.current = { request, load, onError }; });

  const run = useCallback(async (kind: 'refresh' | 'more') => {
    const input = callbacks.current.request;
    if (!input || pending.current) return false;
    const before = latest.current?.key === key ? latest.current.page : undefined;
    if (kind === 'more' && !before?.cursor) return false;
    const active = generation.current;
    const removalRevision = removed.current.revision;
    const token = {};
    pending.current = token;
    setActivity({ key, kind });
    try {
      let page = await callbacks.current.load({ ...input, ...(kind === 'more' && { cursor: before!.cursor! }) });
      if (generation.current !== active) return false;
      const items = kind === 'more' ? [...before!.items, ...page.items] : [...page.items];
      let skipped = kind === 'more' ? [...before!.skipped, ...page.skipped] : [...page.skipped];
      const cursors = new Set<string>();
      let requests = 1;
      while (kind === 'refresh' && items.length < (before?.items.length ?? 0) && page.cursor && !cursors.has(page.cursor) && requests < Math.max(1, before?.items.length ?? 0)) {
        cursors.add(page.cursor);
        requests += 1;
        page = await callbacks.current.load({ ...input, cursor: page.cursor });
        if (generation.current !== active) return false;
        items.push(...page.items);
        skipped.push(...page.skipped);
      }
      skipped = [...new Map(skipped.map(entry => [`${entry.mailbox ?? ''}/${entry.folder ?? ''}/${entry.reason}`, entry])).values()];
      const tombstones = removed.current.key === key ? removed.current.ids : new Set<string>();
      const visible = items.filter(item => !tombstones.has(item.id));
      // An older in-flight response cannot restore successfully moved originals. A fresh
      // chain may release its tombstones only after the native result stops returning them.
      if (kind === 'refresh' && removalRevision === removed.current.revision && !items.some(item => tombstones.has(item.id))) tombstones.clear();
      latest.current = { key, page: { ...page, items: [...new Map(visible.map(item => [item.id, item])).values()], skipped }, failed: false };
      setSnapshot(latest.current);
      return true;
    } catch (error) {
      if (generation.current !== active) return false;
      const retained = latest.current?.key === key ? latest.current.page : before;
      latest.current = { key, ...(retained && { page: retained }), failed: true };
      setSnapshot(latest.current);
      callbacks.current.onError(error);
      return false;
    } finally {
      if (pending.current === token) { pending.current = undefined; setActivity(undefined); }
    }
  }, [key]);

  useEffect(() => {
    const active = {};
    generation.current = active;
    pending.current = active;
    latest.current = undefined;
    removed.current = { key, ids: new Set(), revision: removed.current.revision + 1 };
    const input = callbacks.current.request;
    if (input) callbacks.current.load(input).then(page => {
      if (generation.current !== active) return;
      latest.current = { key, page, failed: false };
      setSnapshot(latest.current);
    }, (error: unknown) => {
      if (generation.current !== active) return;
      latest.current = { key, failed: true };
      setSnapshot(latest.current);
      callbacks.current.onError(error);
    }).finally(() => {
      if (pending.current === active) { pending.current = undefined; setActivity(undefined); }
    });
    else pending.current = undefined;
    return () => { generation.current = {}; };
  }, [key]);
  const refresh = useCallback(() => run('refresh'), [run]);
  const loadMore = useCallback(() => { void run('more'); }, [run]);
  const remove = useCallback((ids: readonly string[]) => {
    if (removed.current.key !== key) return;
    for (const id of ids) removed.current.ids.add(id);
    removed.current.revision += 1;
    if (latest.current?.key !== key || !latest.current.page) return;
    latest.current = { ...latest.current, page: { ...latest.current.page, items: latest.current.page.items.filter(item => !removed.current.ids.has(item.id)) } };
    setSnapshot(latest.current);
  }, [key]);
  const current = snapshot?.key === key ? snapshot : undefined;
  const loading = Boolean(request && !current);
  return { page: current?.page, failed: current?.failed ?? false,
    loading, busy: loading || activity?.key === key, loadingMore: activity?.key === key && activity.kind === 'more',
    refreshing: activity?.key === key && activity.kind === 'refresh', refresh, loadMore, remove };
}

/** Hidden tabs do no arrival work; returning to a folder refreshes immediately. */
export function useMailArrivalRefresh(refresh: () => Promise<void>, enabled: boolean) {
  useEffect(() => {
    if (!enabled) return undefined;
    const tick = () => { if (document.visibilityState !== 'hidden') void refresh(); };
    const timer = window.setInterval(tick, 30_000);
    document.addEventListener('visibilitychange', tick);
    window.addEventListener('focus', tick);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
      window.removeEventListener('focus', tick);
    };
  }, [enabled, refresh]);
}
